import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, MailCheck, MailWarning, Eye, EyeOff } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, formatApiErrorDetail } = useAuth();
  const justSignedUp = location.state?.justSignedUp;
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setUnverifiedEmail("");
    setResendMsg("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "EMAIL_NOT_VERIFIED") {
        setUnverifiedEmail(detail.email || email);
      } else {
        setError(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setResendMsg("");
    try {
      const { data } = await api.post("/auth/resend-verification", { email: unverifiedEmail || email });
      setResendMsg(data.message || "Verification email sent.");
      setResendCooldown(60);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        const m = /(\d+)/.exec(typeof detail === "string" ? detail : "");
        setResendCooldown(m ? parseInt(m[1], 10) : 60);
      }
      setResendMsg(formatApiErrorDetail(detail) || "Couldn't resend right now.");
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell promo="login">
      <div data-testid="login-page">
        <p className="gx-label auth-fade-up">Resume your workspace</p>
        <h2 className="auth-fade-up auth-delay-100 mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl">
          Sign in
        </h2>
        <p className="auth-fade-up auth-delay-200 mt-2 text-sm text-muted-foreground">
          Enterprise access to Pravadh GovernX. Email verification is required.
        </p>

        {justSignedUp && !unverifiedEmail && (
          <div
            className="mt-6 flex items-start gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-400"
            data-testid="signup-success-banner"
          >
            <MailCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Account created. Verify <strong>{email}</strong> before you can sign in.</span>
          </div>
        )}

        {unverifiedEmail && (
          <div
            className="mt-6 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-400"
            data-testid="login-verify-required"
          >
            <div className="flex items-start gap-2.5">
              <MailWarning className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Email verification is mandatory. We sent a link to <strong>{unverifiedEmail}</strong>.</span>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={resend}
                disabled={resending || resendCooldown > 0}
                data-testid="login-resend-button"
              >
                {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend verification email"}
              </Button>
              {resendMsg && <span className="text-xs" data-testid="login-resend-msg">{resendMsg}</span>}
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="auth-fade-up auth-delay-300 mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email">Work email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="h-12 rounded-xl bg-card shadow-sm"
              data-testid="login-email-input"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                to="/forgot-password"
                className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
                data-testid="link-forgot-password"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={show ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-xl bg-card pr-11 shadow-sm"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <p
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="login-error"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-xl text-sm font-semibold shadow-md"
            data-testid="login-submit-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in to GovernX"}
          </Button>

        </form>

        <p className="mt-6 text-sm text-muted-foreground">
          Don't have an account?{" "}
          <Link
            to="/signup"
            className="font-semibold text-primary underline-offset-4 hover:underline"
            data-testid="link-to-signup"
          >
            Start your free trial
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
